import { useMemo, useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconUpload } from '@components/icons/upload';
import { List } from '@components/integrations/integrations-types';
import { Modal, hideModal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCreateList, useListTemplate } from 'api/lists/hooks';
import { ImportColumn } from 'api/types';
import {
  ErrorValidatingCSV,
  ImportToolStepper,
  Instruction,
  LoadingValidatingCSV,
  MatchColumns,
  StepType,
  Table,
  UploadFile,
} from 'ui/authenticated/main/pages/bulk-import/common';

type Option = {
  value: string;
  label: string;
};

type ListFormData = {
  name: string;
};

type Column = ImportColumn<'label' | 'value'>;

const Form = createForm<ListFormData>();

export const useImportListModal = () => useModal(ImportListModal, { variant: 'LARGE' });

export const ImportListModal = ({ lists }: { lists: List[] }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const [step, setStep] = useState<Omit<StepType, 'importing'>>('instruction');
  const [headers, setHeaders] = useState<string[]>([]);
  const [data, setData] = useState([] as { [key: string]: string }[]);
  const [file, setFile] = useState<File | null>(null);
  const [listTitle, setListTitle] = useState('');

  const templateMutation = useListTemplate();
  const createMutation = useCreateList();

  const items: Column[] = [
    {
      id: 'label',
      label: t('label'),
      defaultHeader: t('label'),
      value: '',
      required: true,
      type: 'text',
    },
    {
      id: 'value',
      label: t('value'),
      defaultHeader: t('value'),
      value: '',
      required: true,
      type: 'text',
    },
  ];

  const [columns, setColumns] = useState<Column[]>(items);

  const updateColumnValue = (headerList: string[]) => {
    const newColumns = [...columns];
    newColumns.forEach((column) => {
      if (headerList.find((header) => header === column.defaultHeader)) {
        column.value = column.defaultHeader;
      }
    });
    setColumns(newColumns);
  };

  const getOptions = () => {
    const options: Option[] = [];
    data.forEach((row) => {
      const item = {
        label: '',
        value: '',
      };
      columns.forEach((column) => {
        if (row[column.value]) {
          const columnId = column.id;
          item[columnId] = row[column.value];
        }
      });
      if (item.value && item.label) {
        options.push(item);
      }
    });
    return options;
  };

  const create = async () => {
    await createMutation.mutateAsync({
      name: listTitle,
      source: file?.name ?? '',
      options: getOptions(),
    });
    hideModal();
  };

  const errorList = useMemo(() => {
    const errors: string[] = [];
    const valueColumnValue = columns.find((column) => column.id === 'value')?.value ?? 'value';
    const values = data.map((row) => row[valueColumnValue]);
    data.forEach((row, index) => {
      columns.forEach((column) => {
        const rowNumber = `${index + 1}`;
        const columnName = column.value;

        if (column.required && !row[column.value]) {
          errors.push(t('emptyRowError', { rowNumber, columnName }));
        }
        const currentRowValueIsDuplicate =
          values.filter((value) => value === row[column.value]).length > 1;
        if (currentRowValueIsDuplicate) {
          const error = t('duplicateError', { value: row[column.value] });
          if (!errors.includes(error)) {
            errors.push(error);
          }
        }
      });
    });
    return errors;
  }, [data, columns, t]);

  const hasErrors = errorList.length > 0;

  const onContinue = () => {
    if (lists.some(({ name }) => name === listTitle)) {
      formStore.actions.updateErrors({ name: t('duplicateListTitle') });
      return;
    }
    setStep('uploadFile');
  };

  return (
    <Modal>
      <Modal.SimpleHeader
        title={
          step === 'instruction'
            ? t('importList')
            : step === 'importing'
            ? t('importing')
            : t('csvImportTool')
        }
      />
      <Modal.Content>
        {step === 'instruction' && (
          <Instruction
            setStep={setStep}
            download={templateMutation.mutate}
            templateName={t('templateName')}
            csvTemplateInfo={t('csvTemplateDescription')}
            uploadcsvInfo={t('uploadCSVDescription')}
            helpPageUrl="https://help.50skills.com/en/articles/8826371-bulk-import-lists"
          />
        )}
        {step === 'gettingStarted' && (
          <Stack space={6}>
            <Form store={formStore} onSubmit={onContinue}>
              <ImportToolStepper currentIndex={0} />
              <Stack space={1}>
                <Form.TextField
                  id="name"
                  placeholder={t('listNamePlaceholder')}
                  validators={['required']}
                  label={t('listName')}
                  defaultValue={listTitle}
                  onChange={(e) => setListTitle(e)}
                />
              </Stack>
              <Separator />
              <div>
                <Text text={t('getReadyToImport')} font="bold14" />
                <Text text={t('followingColumns')} font="regular14" color="textSecondary" />
              </div>
              <Table>
                <thead>
                  <tr>
                    <th>{t('label')}</th>
                    <th>{t('value')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t('icelandExample')}</td>
                    <td>{t('isExample')}</td>
                  </tr>
                  <tr>
                    <td>{t('unitedStatesExample')}</td>
                    <td>{t('usExample')}</td>
                  </tr>
                </tbody>
              </Table>
              <Stack space={4} orientation="horizontal" justify="flex-end">
                <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
                <Form.Submit
                  variant="primary"
                  text={t('core.continue')}
                  iconRight={<IconArrowRight />}
                />
              </Stack>
            </Form>
          </Stack>
        )}
        {step === 'uploadFile' && (
          <UploadFile
            onChange={setFile}
            setStep={setStep}
            setHeaders={(value) => {
              setHeaders(value);
              updateColumnValue(value);
            }}
            setData={setData}
          />
        )}
        {step === 'matchColumns' && (
          <MatchColumns
            setStep={setStep}
            headers={headers}
            columns={columns}
            setColumns={setColumns}
          />
        )}
        {step === 'import' && (
          <Stack space={6}>
            <ImportToolStepper currentIndex={3} />
            {hasErrors ? (
              <ErrorValidatingCSV errorList={errorList} />
            ) : createMutation.isLoading ? (
              <LoadingValidatingCSV />
            ) : (
              <Stack justify="center" align="center" space={6}>
                <IconCheckmark color="green100" size={64} />
                <Stack align="center">
                  <Text font="bold16" text={t('validated')} />
                  <Text font="regular14" color="textSecondary" text={t('allDataValid')} />
                </Stack>
              </Stack>
            )}
            <Stack orientation="horizontal" justify="space-between">
              <Button
                variant="secondary"
                text={t('core.back')}
                onClick={() => setStep('uploadFile')}
              />
              <Stack space={4} orientation="horizontal">
                <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
                <Button
                  variant="primary-blue"
                  text={t('import')}
                  iconRight={<IconUpload />}
                  onClick={() => create()}
                  isDisabled={createMutation.isLoading || hasErrors}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    importList: 'Import list',
    duplicateListTitle: 'List with this name already exists',
    csvTemplateDescription: 'Template which includes a row for label and value',
    templateName: 'lists-template.csv',
    uploadCSVDescription: 'Upload the CSV file with the list',
    csvImportTool: 'CSV import tool',
    label: 'Label',
    value: 'Value',
    icelandExample: 'Iceland (Example)',
    unitedStatesExample: 'United States (Example)',
    isExample: 'IS (Example) ',
    usExample: 'US (Example)',
    importing: 'Importing',
    stringText: 'string (text)',
    getReadyToImport: 'Get ready to import your data',
    followingColumns: 'The following columns are required for lists',
    validated: 'Validated!',
    allDataValid: 'All matched rows and data are valid and can be imported',
    import: 'Import',
    importingProcessed: 'Importing is being processed in the background',
    importTime:
      'Please note this can take some time depending on how many options are in the list being added',
    listName: 'List name',
    listNamePlaceholder: 'Enter list name',
    emptyRowError: 'Row {rowNumber} Column {columnName} is empty but is required',
    duplicateError: 'Duplicate values ("{value}") are not allowed',
  },
};
